import { useEffect, useState } from 'react';
import { API_CACHE_TTL, fetchCachedApi } from '@/utils/apiCache';

interface ItunesTrack {
  trackId?: number;
  collectionId?: number;
  wrapperType?: string;
  kind?: string;
  trackName?: string;
  artistName: string;
  collectionName?: string;
  artworkUrl?: string;
  previewUrl?: string;
  trackViewUrl?: string;
  collectionViewUrl?: string;
  releaseDate?: string;
  primaryGenreName?: string;
  trackTimeMillis?: number;
  trackPrice?: number;
  currency?: string;
}

export interface ItunesData {
  resultCount: number;
  tracks: ItunesTrack[];
}

interface UseItunesReturn {
  data: ItunesData | null;
  loading: boolean;
  error: string | null;
}

function useItunes(
  artistName: string,
  trackName: string | null = null,
  limit: number = 10,
  dependencies: unknown[] = [],
  entity: 'song' | 'album' | 'all' = 'song'
): UseItunesReturn {
  const [data, setData] = useState<ItunesData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!artistName) return;

    const fetchItunesData = async () => {
      setLoading(true);
      setError(null);

      try {
        let url = "/.netlify/functions/GetItunesData?artistName=" + encodeURIComponent(artistName) + "&limit=" + limit;
        if (trackName) {
          url += "&trackName=" + encodeURIComponent(trackName);
        }
        url += "&entity=" + encodeURIComponent(entity);

        const json = await fetchCachedApi<ItunesData>(url, {}, {
          ttlMs: API_CACHE_TTL.SIX_HOURS,
          shouldCache: (data) => Array.isArray(data?.tracks) && data.tracks.length > 0,
        });
        setData(json);
      } catch (err) {
        console.error('Error fetching iTunes data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchItunesData();
  }, [artistName, trackName, limit, entity, ...dependencies]);

  return { data, loading, error };
}

export default useItunes;
